import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Input, Space, message, Descriptions, Tag, Modal, QRCode, Divider } from 'antd';
import { UserOutlined, SafetyOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { userApi } from '../services/api';

export default function UserProfile() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [twoFAModalVisible, setTwoFAModalVisible] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableModalVisible, setDisableModalVisible] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => { loadUserInfo(); }, []);

  const loadUserInfo = async () => {
    try {
      const response = await userApi.getMe();
      setUserInfo(response.data?.data || null);
    } catch (error) { message.error(t('loadFailed')); }
  };

  const handleEnable2FA = async () => {
    if (!userInfo?.id) { await loadUserInfo(); message.warning('请稍后重试'); return; }
    setLoading(true);
    try {
      const response = await userApi.enable2FA(userInfo.id);
      const result = response.data;
      if (result.code === '200') { setTwoFAData(result.data); setTwoFAModalVisible(true); }
      else message.error(result.msg || t('twoFAEnableFailed'));
    } catch (error) { message.error(error.response?.data?.msg || t('twoFAEnableFailed')); }
    finally { setLoading(false); }
  };

  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) { message.error(t('enter6Code')); return; }
    setLoading(true);
    try {
      await userApi.verify2FA(userInfo.id, verifyCode);
      message.success(t('twoFAEnabled'));
      setTwoFAModalVisible(false); setVerifyCode(''); setTwoFAData(null); loadUserInfo();
    } catch (error) { message.error(error.response?.data?.msg || t('twoFAVerifyFailed')); }
    finally { setLoading(false); }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) { message.error(t('enter6Code')); return; }
    setLoading(true);
    try {
      await userApi.disable2FA(userInfo.id, disableCode);
      message.success(t('twoFADisabled'));
      setDisableModalVisible(false); setDisableCode(''); loadUserInfo();
    } catch (error) { message.error(error.response?.data?.msg || t('twoFADisableFailed')); }
    finally { setLoading(false); }
  };

  if (!userInfo) return <div style={{ padding: 24 }}>{t('loading')}</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1000, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card className="modern-card"
          title={<Space size={16}><UserOutlined style={{ color: '#667eea', fontSize: 20 }} /><span style={{ fontSize: 20, fontWeight: 600 }}>{t('personalSettings')}</span></Space>}>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('userId')}><span style={{ fontFamily: 'monospace' }}>{userInfo.uid}</span></Descriptions.Item>
            <Descriptions.Item label={t('username')}>{userInfo.username}</Descriptions.Item>
            <Descriptions.Item label={t('role')}>
              {userInfo.role === 'ADMIN' ? <Tag color="gold">{t('admin')}</Tag> : <Tag color="blue">{t('normalUser')}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('userType')}>
              {userInfo.userType === 'INTERNAL' ? <Tag color="cyan">🏢 {t('internalUser')}</Tag> : <Tag color="orange">🌐 {t('externalUser')}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('userStatus')}>
              <Tag color={userInfo.status === 'ACTIVE' ? 'success' : 'default'}>{userInfo.status === 'ACTIVE' ? t('active') : t('disabled')}</Tag>
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Card type="inner"
            title={<Space><SafetyOutlined style={{ color: '#52c41a' }} /><span>{t('twoFATitle')}</span></Space>}
            extra={userInfo.twoFactorEnabled ? <Tag icon={<CheckCircleOutlined />} color="success">{t('twoFAEnabled2')}</Tag> : <Tag color="default">{t('twoFADisabled2')}</Tag>}>
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#666', marginBottom: 8 }}>{t('twoFAProtection')}</p>
              <p style={{ color: '#999', fontSize: 12 }}>{t('twoFARecommend')}</p>
            </div>
            {!userInfo.twoFactorEnabled ? (
              <Button type="primary" icon={<SafetyOutlined />} onClick={handleEnable2FA} loading={loading}>{t('enable2FA')}</Button>
            ) : (
              <Button danger icon={<LockOutlined />} onClick={() => setDisableModalVisible(true)}>{t('disable2FA')}</Button>
            )}
          </Card>
        </Card>

        <Modal
          title={<Space><SafetyOutlined style={{ color: '#52c41a' }} /><span>{t('twoFATitle')}</span></Space>}
          open={twoFAModalVisible}
          onCancel={() => { setTwoFAModalVisible(false); setVerifyCode(''); setTwoFAData(null); }}
          onOk={handleVerify2FA} okText={t('verifyAndEnable')} cancelText={t('cancel')} confirmLoading={loading} width={500}>
          {twoFAData && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}><strong>{t('twoFAStep1')}</strong></p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <QRCode value={twoFAData.qrCodeUrl} size={200} />
              </div>
              <div style={{ marginBottom: 24, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{t('manualKeyDesc')}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' }}>{twoFAData.secret}</div>
              </div>
              <p style={{ marginBottom: 8, fontSize: 14, color: '#666' }}><strong>{t('twoFAStep2')}</strong></p>
              <Input placeholder={t('enter6Code')} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)}
                maxLength={6} size="large" style={{ textAlign: 'center', fontSize: 20, letterSpacing: 8 }} />
            </div>
          )}
        </Modal>

        <Modal
          title={<Space><LockOutlined style={{ color: '#ff4d4f' }} /><span>{t('disable2FA')}</span></Space>}
          open={disableModalVisible}
          onCancel={() => { setDisableModalVisible(false); setDisableCode(''); }}
          onOk={handleDisable2FA} okText={t('confirmDisable')} okButtonProps={{ danger: true }}
          cancelText={t('cancel')} confirmLoading={loading} width={400}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: 16, color: '#666' }}>{t('disableTwoFADesc')}</p>
            <Input placeholder={t('enter6Code')} value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
              maxLength={6} size="large" style={{ textAlign: 'center', fontSize: 20, letterSpacing: 8 }} />
          </div>
        </Modal>
      </div>
    </div>
  );
}
